import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { darken, styled, alpha } from "@mui/material/styles";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  tooltipClasses,
  Typography,
  Zoom
} from "@mui/material";
import { useTheme } from "@mui/styles";

import {
  Search,
  ArrowUpward,
  ArrowDownward,
  OpenInNew,
  ExpandLess,
  ExpandMore,
  KeyboardArrowUp,
  Clear,
  ZoomIn as ZoomInIcon
} from "@mui/icons-material"

import LightBoxModal from "../light-box/light-box-modal";
import CommonSearchPaginateSkeleton from "../client-portal/common-search-paginate-skeleton";
import CommonPaginateSkeleton from "../client-portal/common-paginate-skeleton";
import CustomIcon from "../custom-icon";
import CommonTotalPagesCount from "../client-portal/common-total-pages-count";

const FilterStyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "customcolor",
})(({ customcolor }) => ({
  backgroundColor: `${alpha(customcolor, 0.1)} !important`, // 20 = 12% opacity no hex
  borderColor: customcolor,
  color: `${customcolor} !important`,
  fontWeight: 500,

  // Ícone de delete (x)
  "& .MuiChip-deleteIcon": {
    color: `${customcolor} !important`,
    "&:hover": {
      color: `${customcolor} !important`,
      opacity: 0.7,
    },
  },

  "&:hover": {
    backgroundColor: `${alpha(customcolor, 0.3)} !important`,
  },
}));

const FilterStyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "defaultcolor",
})(({ defaultcolor }) => ({
  backgroundColor: "#fff !important",
  borderRadius: "12px",

  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    height: "48px !important",

    // Hover — seletor mais específico
    "&:not(.Mui-disabled):not(.Mui-error):hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "1px",
    },

    // Focused — seletor mais específico
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "2px",
    },

    // Disabled
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "1px",
      opacity: 0.5,
    },

    // Error
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultcolor} !important`,
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    padding: "12px 16px",
    fontSize: "0.95rem",
  },
}));

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) =>
    ![
      "iconColor",
      "customBorderColor"
    ].includes(prop),
})(
  ({
    iconColor,
    customBorderColor
  }) => ({
    "& .MuiOutlinedInput-root": {
      height: "48px !important",
      backgroundColor: "#fff !important",
      borderRadius: "8px",
      "&.Mui-focused fieldset": {
        border: `2px solid ${customBorderColor}`,
      },
    },

    "& .MuiSvgIcon-root": {
      fill: iconColor,
    },
  })
);

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) =>
    !["customHoverBgColor", "customHoverTextColor", "customSelectedBgColor", "customSelectedTextColor"].includes(prop),
})(
  ({ customHoverBgColor, customHoverTextColor, customSelectedBgColor, customSelectedTextColor }) => ({
    "&:hover": {
      backgroundColor: customHoverBgColor,
      color: customHoverTextColor,
    },
    "&.Mui-selected": {
      backgroundColor: customSelectedBgColor,
      color: customSelectedTextColor,
    },
    "&.Mui-selected:hover": {
      backgroundColor: customHoverBgColor,
      color: customHoverTextColor,
    },
  })
);

const CustomButton = styled(Button)(() => ({
  color: "#fff !important",
  borderRadius: "8px",
  padding: "12px 24px",

  "&:disabled": {
    backgroundColor: "#d1d5db !important",
    color: "initial !important",
  },
}));

const ClearButton = styled(Button)(({ getCustomColorPrimary }) => ({
  color: getCustomColorPrimary(0.5),
  borderRadius: "8px",
  padding: "12px 24px",
  borderColor: getCustomColorPrimary(),

  "&:hover": {
    borderColor: getCustomColorPrimary(0.1),
    backgroundColor: `${alpha(getCustomColorPrimary(0, true), 0.1)} !important`,
  },

  "&:disabled": {
    backgroundColor: "#d1d5db !important",
    borderColor: "#d1d5db !important",
  },
}));

const MainCard = styled(Card)({
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  border: "1px solid #e5e7eb",
})

const AttachmentItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "8px 12px",
  margin: "4px 8px 4px 0",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#f1f5f9",
    borderColor: "#cbd5e1",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
})

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip describeChild {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

// CustomSelect agora aceita funções para customizar cor do texto e hover do MenuItem
const CustomSelect = ({
  value,
  handleChange,
  label,
  id,
  menuItemsList = [],
  getCustomColorPrimary
}) => {
  const accentColorWithImportant = getCustomColorPrimary();
  const accentColorWithoutImportant = getCustomColorPrimary(0, true);

  const accentBackgroundColor = alpha(accentColorWithoutImportant, 0.15);

  return (
    <StyledFormControl
      fullWidth
      size="small"
      iconColor={accentColorWithoutImportant}
      customBorderColor={accentColorWithImportant}
    >
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value}
        onChange={handleChange}
        label={label}
      >
        {menuItemsList.map((item) => {
          return (
            <StyledMenuItem
              key={item.value}
              value={item.value}
              customSelectedTextColor={accentColorWithImportant}
              customSelectedBgColor={`${accentBackgroundColor} !important`}

              customHoverTextColor={accentColorWithImportant}
              customHoverBgColor={`${accentBackgroundColor} !important`}
            >
              {item.label}
            </StyledMenuItem>
          );
        })}
      </Select>
    </StyledFormControl>
  )
}

export default function FAQExternalClientMainContent({
  topRef = false,
  state,
  loading = false,
  loadingFaqs = false,

  handleSearchChange,
  onChangePaginate,
  handleSelectedFaq,
  getTagColor,
  getFileAttachment,
  handleSearch,
  clearFaqs,
  handleClickOpenSubjectToFaqs,
  goToScrollTop,
  addFilter,
  handleDelete,

  AttachmentIcon = () => null,

}) {
  const contentRef = useRef(null);
  const intl = useIntl();
  const theme = useTheme();

  const primaryColor = theme.palette.primary.main;
  const _defaultClientColor = state.clientportal?.defaultcolor ? state.clientportal?.defaultcolor : null;
  const defaultClientColor = _defaultClientColor || primaryColor;

  const accentIconColor = getCustomColorPrimary(0, true);

  const [showScrollTop, setShowScrollTop] = useState(false);

  const searchValueListFilterArray = (state?.searchValueList || []).filter(i => i.trim() != "");
  const selectedSubjectListFilterArray = (state?.selectedSubjectList || []).filter(i => !!i);

  const arrayToDisabledClearFilter = [
    loading,
    loadingFaqs,
    !!state.searchValue,
    state.selectedSubject !== "all",
    !!searchValueListFilterArray.length,
    !!selectedSubjectListFilterArray.length
  ];

  const disabledClearFilter = arrayToDisabledClearFilter.some(item => item === true);

  const {
    clientportal,
    selectedFaq,
    searchValue,
    selectedSubject,
    sortBy,
    sortOrder,
    subjectList,
    filteredFaqs
  } = state;

  const stripHtml = (html) => {
    // Substitui tags de quebra de linha por \n antes de remover o HTML
    let processedHtml = html
      .replace(/<br\s*\/?>/gi, '\n')
    const doc = new DOMParser().parseFromString(processedHtml, 'text/html');
    let text = doc.body.textContent || '';

    // Normaliza quebras de linha: remove múltiplas quebras consecutivas (mantém máximo de 2)
    text = text
      .replace(/\u00A0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return text;
  };

  function getCustomColorPrimary(darkenValue = 0, customIcon = false) {
    const customColor = darken(defaultClientColor, darkenValue);

    if (customIcon) return customColor;

    return `${customColor} !important`;
  }

  function getCommonButtonStyleWithHover(options = {}) {
    const {
      mainBackgroundColor = getCustomColorPrimary(0.1),
      hoverBackgroundColor = getCustomColorPrimary(0.2),
      mainCss = {},
      hoverCss = {}
    } = options;

    return {
      backgroundColor: mainBackgroundColor,
      ...mainCss,
      ":hover": {
        backgroundColor: hoverBackgroundColor,
        ...hoverCss
      }
    }
  }

  function renderFaqsCollapseToSubject(props) {
    const defaultClientColor = clientportal.defaultcolor ? clientportal.defaultcolor : null;

    const { id, question } = props;

    const currentFaqId = selectedFaq.id;

    const isActive = id === currentFaqId;

    const isActiveProps = isActive
      ? { style: { cursor: "default" } }
      : { onClick: () => handleSelectedFaq(id) };

    return (
      <li>
        <ListItemButton
          {...isActiveProps}
          sx={{
            pl: 4,
          }}>
          <Tooltip title={question} placement="right" arrow>
            <Typography
              variant="h6"
              children={question}
              sx={{
                color: isActive && defaultClientColor ? defaultClientColor : "primary",
                fontWeight: isActive ? 600 : 300
              }}
            />
          </Tooltip>
        </ListItemButton>
      </li>
    );
  }

  const renderFaqLabel = (props) => {
    const defaultClientColor = clientportal.defaultcolor ? clientportal.defaultcolor : null;

    const { id, subjectId, subjectName, open, mapGroudFaqFromSubject, subject } = props;

    const description = subject ? subject.description : subjectName;

    const hasMapGroudFaqFromSubject = mapGroudFaqFromSubject && mapGroudFaqFromSubject.length > 0;

    const isActiveSubjectTextProps = selectedFaq?.subjectid == subjectId
      ? {
        sx: {
          width: '100%',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }
      }
      : {
        sx: {
          width: '100%',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }
      };

    if (hasMapGroudFaqFromSubject) {
      let icon = null;
      if (open != null && hasMapGroudFaqFromSubject) {
        icon = open
          ? <CustomIcon icon={ExpandLess} color={defaultClientColor} />
          : <CustomIcon icon={ExpandMore} color={defaultClientColor} />;
      }

      return (
        <React.Fragment key={id}>
          <li>
            <ListItemButton
              sx={{ justifyContent: 'space-between' }}
              onClick={() => handleClickOpenSubjectToFaqs(subjectId)}
            >
              <CustomWidthTooltip title={stripHtml(description)} placement="top" arrow>
                <Typography variant="h6" children={subjectName} {...isActiveSubjectTextProps} />
              </CustomWidthTooltip>

              {icon}
            </ListItemButton>
          </li>

          <Collapse component="li" in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {mapGroudFaqFromSubject.map((faq) => renderFaqsCollapseToSubject(faq))}
            </List>
          </Collapse>
        </React.Fragment>
      )
    }

    return (
      <li
        key={id}
        style={{
          paddingBottom: "8px",
          "&:last-child": {
            paddingBottom: 0,
          }
        }}
      >
        <ListItemButton onClick={() => handleSelectedFaq(id)} sx={{ borderRadius: 2 }}>
          <Tooltip title={stripHtml(description)} placement="top" arrow>
            <Typography
              children={subjectName}
              variant="h6"
              {...isActiveSubjectTextProps}
            />
          </Tooltip>
        </ListItemButton>
      </li>
    );
  }

  const renderFaqSections = () => {

    return (
      <List sx={{ width: '100%' }} component="nav" aria-labelledby="nested-list-subheader">
        {filteredFaqs.map(renderFaqLabel)}
      </List>
    )
  }

  const renderFaqBody = () => {
    const faq = selectedFaq;

    if (!!!faq) return;

    const externallink = !!faq.externallink && faq.externallink != "undefined" && faq.externallink != "null"
      ? faq.externallink : null;

    return (
      <Box width="100%" id={`faq-${faq.id}`}>
        <Typography
          variant="body2"
          sx={{
            color: "#4b5563",
            lineHeight: 1.6,
            fontSize: "0.9rem",
            mb: 3,
          }}
          component="div"
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: faq.answer }}
        />

        {externallink && (
          <Box sx={{ mb: 3 }} display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
            <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1, display: "block" }}>
              {intl.formatMessage({ id: "faq.external.link" })}
            </Typography>

            <CustomButton
              href={externallink}
              target="_blank"
              rel="noopener noreferrer"
              sx={getCommonButtonStyleWithHover()}
            >
              {intl.formatMessage({ id: "faq.external.link.access" })}
            </CustomButton>
          </Box>
        )}

        {faq.faqtags && faq.faqtags.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 1,
                display: "block",
              }}
            >
              Tags
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {faq.faqtags.map((tag) => {
                const tagColor = getTagColor(tag.tagname)

                return (
                  <Chip
                    key={tag.id}
                    label={tag.tagname}
                    size="small"
                    variant="outlined"
                    onClick={() => handleSearchChange(tag.tagname, "tag")}
                    sx={{
                      backgroundColor: tagColor.backgroundColor,
                      color: tagColor.color,
                      border: `1px solid ${tagColor.border}`,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      height: "24px",
                      "&:hover": {
                        filter: "brightness(0.9)",
                      },
                      transition: "all 0.3s ease-in-out",
                      cursor: "pointer",
                    }}
                  />
                )
              })}
            </Box>
          </Box>
        )}

        {faq.faqattachments && faq.faqattachments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#6b7280",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 2,
                  display: "block",
                }}
              >
                Anexos ({faq.faqattachments.length})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {faq.faqattachments.map((attachment) => (
                  <AttachmentItem key={attachment.id} onClick={() => getFileAttachment(attachment)}>
                    <AttachmentIcon filename={attachment.name} />
                    <Box sx={{ ml: 1, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#374151",
                          lineHeight: 1.2,
                        }}
                      >
                        {attachment.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontSize: "0.7rem",
                        }}
                      >
                        {attachment.size}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      sx={{
                        ml: 1,
                        color: "#6366f1",
                        "&:hover": {
                          backgroundColor: "#e0e7ff",
                        },
                      }}
                    >
                      <CustomIcon icon={OpenInNew} color={getCustomColorPrimary(0, true)} iconSx={{ fontSize: "medium" }} />
                    </IconButton>
                  </AttachmentItem>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>

    )
  }

  useEffect(() => {
    const scrollElement = topRef?.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;
      const scrollableHeight = scrollHeight - clientHeight;
      const scrollTop = scrollElement.scrollTop;
      const scrolledPastHalf = scrollTop > (scrollableHeight * 0.5);
      setShowScrollTop(scrolledPastHalf);
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [topRef]);

  return (
    <React.Fragment>
      {(state?.searchValueList?.length > 0 || state?.selectedSubjectList?.length > 0) &&
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems="center" >
                <Grid item xs={8} md={6}>
                  <Typography variant="h5">
                    {intl.formatMessage({ id: "filters" })}
                  </Typography>
                </Grid>

                {state?.searchValueList?.length > 0 && (
                  <Grid container item xs={12} md={3} direction={"column"} spa={1}>
                    <Typography variant="body1" fontWeight={"600"}>
                      {intl.formatMessage({ id: "active.search.filters" })}:
                    </Typography>

                    <strong style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {(state?.searchValueList || []).map((i, key) => {
                        if (i.trim() != "") {
                          return <FilterStyledChip
                            key={key}
                            label={i}
                            onDelete={() => handleDelete(key, "search")}
                            color="primary"
                            variant="outlined"
                            customcolor={getCustomColorPrimary(0, true)}
                          />
                        }
                      })}
                    </strong>
                  </Grid>

                )}

                {state?.selectedSubjectList?.length > 0 && (
                  <Grid container item xs={12} md={3} direction={"column"} gap={1}>
                    <Typography variant="body1" fontWeight={"600"}>
                      {intl.formatMessage({ id: "active.subject.filters" })}:
                    </Typography>

                    <strong style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {(state?.selectedSubjectList || []).map((i, key) => {
                        const getSubject = (state?.subjectList || []).find(subject => subject?.id?.toString() == i);

                        return <FilterStyledChip
                          key={key}
                          label={getSubject ? getSubject.name : i}
                          onDelete={() => handleDelete(key, "subject")}
                          color="primary"
                          variant="outlined"
                          customcolor={getCustomColorPrimary(0, true)}
                        />

                      })}
                    </strong>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Container>
      }

      <Container maxWidth="lg" sx={{ py: 4, px: 3, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Seção de filtros e busca */}
        <CommonSearchPaginateSkeleton loading={loading} />

        {!loading && (
          <MainCard sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={1.5} sx={12} alignItems="center">
                {/* Campo de busca */}
                <Grid item xs={12} md={4}>
                  <FilterStyledTextField
                    name="searchValue"
                    fullWidth
                    placeholder="Buscar por perguntas ou respostas"
                    value={searchValue}
                    onChange={handleSearchChange}
                    variant="outlined"
                    size="small"
                    defaultcolor={getCustomColorPrimary(0, true)}
                  />
                </Grid>

                {/* Filtro por assunto */}
                <Grid item xs={12} md={1.5}>
                  <CustomSelect
                    id="subject-select"
                    label="Assunto"
                    value={selectedSubject}
                    handleChange={(e) => handleSearchChange(e, "selectedSubject")}
                    getCustomColorPrimary={getCustomColorPrimary}
                    menuItemsList={[{ value: "all", label: "- Qualquer -" }, ...subjectList.map((subject) => ({ value: subject.id.toString(), label: subject.name }))]}
                  />
                </Grid>

                {/* Ordenação por */}
                <Grid item xs={12} md={1.5}>
                  <CustomSelect
                    id="sort-select"
                    label="Ordenar por"
                    value={sortBy}
                    getCustomColorPrimary={getCustomColorPrimary}
                    handleChange={(e) => handleSearchChange(e, "sortBy")}
                    menuItemsList={[{ value: "date", label: "Data" }, { value: "name", label: "Nome" }]}
                  />
                </Grid>

                {/* Ordem */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    id="order-select"
                    label="Ordem"
                    value={sortOrder}
                    getCustomColorPrimary={getCustomColorPrimary}
                    handleChange={(e) => handleSearchChange(e, "sortOrder")}
                    menuItemsList={[
                      {
                        value: "asc",
                        label: (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CustomIcon icon={ArrowUpward} color={accentIconColor} iconSx={{ fontSize: "medium" }} />
                            Crescente
                          </Box>
                        )
                      },
                      {
                        value: "desc",
                        label: (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CustomIcon icon={ArrowDownward} color={accentIconColor} iconSx={{ fontSize: "medium" }} />
                            Decrescente
                          </Box>
                        )
                      }]}
                  />
                </Grid>

                {/* { Botão de adicionar filtro } */}
                <Grid item xs={12} md={1}>
                  <CustomButton
                    fullWidth
                    onClick={addFilter}
                    sx={getCommonButtonStyleWithHover()}
                    disabled={loading || loadingFaqs || (!searchValue && selectedSubject === "all")}
                  >
                    <CustomIcon icon={ZoomInIcon} color="#fff" />
                  </CustomButton>
                </Grid>

                {/* Botão de busca */}
                <Grid item xs={12} md={1}>
                  <CustomButton
                    fullWidth
                    onClick={handleSearch}
                    sx={getCommonButtonStyleWithHover()}
                    disabled={loading || loadingFaqs}
                  >
                    <CustomIcon icon={Search} color="#fff" />
                  </CustomButton>
                </Grid>

                {/* { Botão de limpar } */}
                <Grid item xs={12} md={1}>
                  <ClearButton
                    variant="outlined"
                    fullWidth
                    onClick={clearFaqs}
                    getCustomColorPrimary={getCustomColorPrimary}
                    disabled={!disabledClearFilter}
                  >
                    <CustomIcon icon={Clear} color={getCustomColorPrimary(0.5, true)} />
                  </ClearButton>
                </Grid>
              </Grid>
            </CardContent>
          </MainCard>
        )}

        <CommonPaginateSkeleton loading={loading || loadingFaqs} />

        {(!loading && !loadingFaqs) && (
          <React.Fragment>
            {/* Lista de FAQs */}
            <MainCard>
              {filteredFaqs.length > 0 && (
                <Grid container sx={{ width: "100%" }} spacing={0}>
                  <Grid container item xs={2.5} pt={2} pb={2} pr={0} pl={2}>
                    {renderFaqSections()}
                  </Grid>

                  <Grid container item xs={9.5} pt={4} pb={4} pl={2} pr={3}>
                    {renderFaqBody()}
                  </Grid>
                </Grid>
              )}

              {filteredFaqs.length <= 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    textAlign: 'center',
                    borderRadius: '12px',
                    width: '100%',
                    maxWidth: '500px',
                    margin: '40px auto',
                  }}
                >
                  <CustomIcon icon={Search} color={defaultClientColor} iconSx={{ fontSize: 64, marginBottom: '16px' }} />
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#374151',
                      fontWeight: 600,
                      marginBottom: '8px'
                    }}
                  >
                    <FormattedMessage id="faq.content.not.found" />
                  </Typography>
                </Box>
              )}
            </MainCard>

            {filteredFaqs.length > 0 && (
              <Grid container style={{ marginBottom: 12, marginTop: 8 }}>
                <CommonTotalPagesCount state={state} onChangePaginate={onChangePaginate} clientportal={clientportal} />
              </Grid>
            )}
          </React.Fragment>
        )}
      </Container>

      {/* Lightbox Modal para ampliar imagens */}
      <LightBoxModal
        contentRef={contentRef}
        tooltip={intl.formatMessage({ id: "click.to.enlarge" })}
        dependency={selectedFaq?.answer}
      />

      {/* Botão flutuante para voltar ao topo */}
      <Zoom in={showScrollTop} unmountOnExit>
        <Fab
          size="medium"
          onClick={goToScrollTop}
          sx={{
            position: 'fixed',
            bottom: 28,
            right: 32,
            zIndex: 1000,
            backgroundColor: clientportal.defaultcolor ? `${clientportal.defaultcolor} !important` : undefined,
            '&:hover': {
              filter: 'brightness(0.9)',
            },
          }}
          aria-label="scroll to top"
        >
          <CustomIcon icon={KeyboardArrowUp} color="#fff" />
        </Fab>
      </Zoom>
    </React.Fragment>
  )
}